import {
  AppDataComponent,
  DocumentServicesObject,
  EditorClientSpecMapEntry,
  UnifiedWidget,
  WidgetPluginComponent,
} from '@wix/document-services-types';
import { Item, Section } from '@wix/editor-platform-host-integration-apis';
import {
  ComponentType,
  Preset,
} from '@wix/ambassador-devcenter-components-v1-component/types';
import { PlatformContext } from '../../../types/platformApi';
import { ALLOWED_EXTENSIONS } from './constants';
import {
  AppDataComponentWithItems,
  PlatformAppCategoryDefinition,
  PlatformAppItemDefinition,
  PlatformAppWithCategoryDefinition,
} from './types';
import { AppData } from '../../../types/unifiedComponents';
import { mapDefinitionToItem, mapDefinitionToSection } from './mappers';
import {
  getPageDataByComponentId,
  isComponentEssential,
} from '../unifiedComponents/componentSettings';

export const isValidUnifiedPreset = (
  preset?: Partial<Preset>,
): preset is Preset =>
  Boolean(preset && preset.id && preset.name && preset.thumbnailUrl);

export const isValidBlocksPresetSize = (
  size?: Partial<PlatformAppItemDefinition['size']>,
): size is PlatformAppItemDefinition['size'] =>
  Boolean(size && size.width && size.height);

export const isValidBlocksPreset = (
  preset?: PlatformAppCategoryDefinition['items'][number],
): preset is PlatformAppItemDefinition =>
  Boolean(
    preset &&
      preset.widgetId &&
      preset.presetId &&
      preset.desc &&
      preset.image &&
      isValidBlocksPresetSize(preset.size),
  );

export const isValidPreset = (
  preset?: Partial<Preset> | PlatformAppCategoryDefinition['items'][number],
): preset is Preset | PlatformAppItemDefinition =>
  isValidUnifiedPreset(preset) || isValidBlocksPreset(preset);

export const getPresetsToShowInAddPanel = (
  component: AppDataComponent,
  addPanelDefinitionForBlocks?: PlatformAppCategoryDefinition,
): Preset[] | PlatformAppItemDefinition[] =>
  (
    (component.data as UnifiedWidget)?.presets ??
    addPanelDefinitionForBlocks?.items?.filter(
      (item) => item.widgetId === component.componentId,
    ) ??
    []
  ).filter(isValidPreset);

export const getItems = (
  documentServices: DocumentServicesObject,
  context: PlatformContext,
  appData: AppData,
  component: AppDataComponent,
  addPanelDefinitionForBlocks?: PlatformAppCategoryDefinition,
): Item[] => {
  const presets = getPresetsToShowInAddPanel(
    component,
    addPanelDefinitionForBlocks,
  );

  return presets.map((preset) =>
    mapDefinitionToItem(
      documentServices,
      context,
      appData,
      component,
      preset,
      presets,
    ),
  );
};

export const isExtensionAllowed = (compType?: ComponentType) =>
  Boolean(compType && ALLOWED_EXTENSIONS.includes(compType));

export const shouldBeAdded = (
  context: PlatformContext,
  appData: AppData,
  { type, componentId, data }: AppDataComponent = {},
  plugins: string[] = [],
): boolean => {
  if (!type || !componentId || !data) {
    return false;
  }

  if (!isExtensionAllowed(type as ComponentType)) {
    return false;
  }

  if (plugins.includes(componentId)) {
    return false;
  }

  const pageData = getPageDataByComponentId(appData, componentId);
  const tpaPageId = pageData?.base?.id;

  const shouldReadDataFromNewSchema = context.experimentsAPI.enabled(
    'specs.editor-platform.unified-components-proto-changes',
  );

  return !isComponentEssential(
    componentId,
    appData,
    shouldReadDataFromNewSchema,
    tpaPageId,
  );
};

export const getAddPanelDefinitionForBlocks = (
  documentServices: DocumentServicesObject,
  appId: string,
): PlatformAppCategoryDefinition | undefined => {
  const appManifest: PlatformAppWithCategoryDefinition =
    documentServices.platform.getAppManifest(appId);
  return appManifest?.addPanelSectionDefinition;
};

export const getComponentsByOrder = (
  appData: AppData,
  addPanelDefinitionForBlocks: PlatformAppCategoryDefinition | undefined,
): AppDataComponent[] => {
  if (!addPanelDefinitionForBlocks) {
    return appData.components;
  }

  const addedBlocksComponentIds = new Set<string>();

  const orderedBlocksComponents = (
    addPanelDefinitionForBlocks.items ?? []
  ).reduce<AppDataComponent[]>((uniqueComponents, blocksWidget) => {
    if (
      blocksWidget.widgetId &&
      !addedBlocksComponentIds.has(blocksWidget.widgetId)
    ) {
      const component = appData.components.find(
        (comp) => comp.componentId === blocksWidget.widgetId,
      );

      if (component) {
        uniqueComponents.push(component);
        addedBlocksComponentIds.add(blocksWidget.widgetId);
      }
    }

    return uniqueComponents;
  }, []);

  const remainingComponents = appData.components.filter(
    (component) => !addedBlocksComponentIds.has(component.componentId ?? ''),
  );

  return [...orderedBlocksComponents, ...remainingComponents];
};

export const getPlugins = (appData: AppData): string[] =>
  appData.components
    .filter(
      (component) =>
        component.type === ComponentType.WIDGET_PLUGIN && component.data,
    )
    .map(
      (component) =>
        (component as WidgetPluginComponent).data.referenceComponentId,
    );

export const getSections = (
  documentServices: DocumentServicesObject,
  context: PlatformContext,
  app: EditorClientSpecMapEntry,
): Section[] => {
  const appData = documentServices.platform.getAppDataByAppDefId(
    app.appDefinitionId,
  ) as Required<AppData>;
  if (!appData?.components?.length) {
    return [];
  }

  const addPanelDefinitionForBlocks = getAddPanelDefinitionForBlocks(
    documentServices,
    app.appDefinitionId,
  );
  const components = getComponentsByOrder(appData, addPanelDefinitionForBlocks);
  const plugins = getPlugins(appData);

  return components
    .filter((component: AppDataComponent) =>
      shouldBeAdded(context, appData, component, plugins),
    )
    .map((component: AppDataComponent) => ({
      component,
      items: getItems(
        documentServices,
        context,
        appData,
        component,
        addPanelDefinitionForBlocks,
      ),
    }))
    .filter(
      (componentWithItems: AppDataComponentWithItems) =>
        componentWithItems.items.length,
    )
    .map((componentWithItems: AppDataComponentWithItems, index) =>
      mapDefinitionToSection(componentWithItems, index),
    );
};
